import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  Check, Loader2, Zap, BarChart3, Bell, 
  HeadphonesIcon, Shield, Clock, Sparkles, ArrowLeft
} from 'lucide-react';
import MarketingHeader from '../components/MarketingHeader';
import SEOHead from '../components/SEOHead';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Pricing = () => {
  const { user, token, isPremium, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [plans, setPlans] = useState([]);
  const [freeFeatures, setFreeFeatures] = useState([]);
  const [billingCycle, setBillingCycle] = useState('annual');

  // SEO structured data for pricing page
  const pricingStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "ScopeTheMarket Pricing",
    "description": "Choose your S&P 500 stock scanner plan. Free tier for basic scans, Pro for unlimited anomaly detection, AI analysis, alerts, and watchlists.",
    "url": "https://scopethemarket.com/pricing",
    "mainEntity": {
      "@type": "Product",
      "name": "ScopeTheMarket Pro",
      "description": "Unlimited S&P 500 stock scanning with AI-powered analysis, real-time alerts, and custom watchlists",
      "offers": [
        {
          "@type": "Offer",
          "name": "Monthly Plan",
          "price": "14.99",
          "priceCurrency": "USD",
          "priceValidUntil": "2027-12-31",
          "availability": "https://schema.org/InStock"
        },
        {
          "@type": "Offer",
          "name": "Annual Plan",
          "price": "99.99",
          "priceCurrency": "USD",
          "priceValidUntil": "2027-12-31",
          "availability": "https://schema.org/InStock"
        }
      ]
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/billing/plans`);
      setPlans(response.data.plans);
      setFreeFeatures(response.data.free_features);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleUpgrade = async (planId) => {
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }

    setLoading(planId);

    try {
      // Use the standard checkout endpoint with user auth
      const response = await axios.post(
        `${API_BASE}/api/billing/checkout`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      setLoading(null);
    }
  };

  // Alternative: Simple checkout without auth (uses /create-checkout-session)
  const handleSimpleCheckout = async () => {
    setLoading('simple');
    try {
      const response = await axios.post(`${API_BASE}/api/create-checkout-session`);
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      setLoading(null);
    }
  };

  const proFeatures = [
    { icon: Zap, text: 'Unlimited S&P 500 stock scans' },
    { icon: BarChart3, text: 'Unlimited AI stock article analysis' },
    { icon: Bell, text: 'Real-time stock anomaly alerts' },
    { icon: HeadphonesIcon, text: 'Priority support' },
    { icon: Shield, text: 'Advanced stock analytics dashboard' }
  ];

  const monthlyPlan = plans.find(p => p.id === 'pro_monthly');
  const annualPlan = plans.find(p => p.id === 'pro_annual');

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0B0F1A' }}>
      <SEOHead
        title="Pricing | S&P 500 Stock Scanner Plans | ScopeTheMarket"
        description="Choose your ScopeTheMarket plan. Free tier for basic S&P 500 scans, Pro for unlimited anomaly detection, AI analysis, real-time alerts, and custom watchlists."
        canonical="https://scopethemarket.com/pricing"
        ogTitle="ScopeTheMarket Pricing – Stock Scanner Plans"
        ogDescription="Unlimited S&P 500 scanning with AI analysis, alerts, and watchlists. Start free or go Pro."
        structuredData={pricingStructuredData}
      />
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-[600px] h-[600px] rounded-full blur-3xl opacity-15"
          style={{
            background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)',
            top: '-200px',
            left: '50%',
            transform: 'translateX(-50%)'
          }}
        />
      </div>

      {/* Auth-aware Marketing Header */}
      <MarketingHeader 
        navLinks={[
          { to: '/pricing', label: 'Pricing' },
          { to: '/about', label: 'About' },
          { to: '/blog', label: 'Blog' }
        ]}
      />

      {/* Main Content */}
      <div className="relative z-10 py-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Back link */}
          <Link 
            to="/"
            className="inline-flex items-center gap-2 mb-8 text-sm transition-colors hover:text-white"
            style={{ color: '#64748B' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Header */}
          <div className="text-center mb-16">
            <h1 
              className="text-4xl sm:text-5xl font-bold mb-4"
              style={{ color: '#F8FAFC' }}
            >
              S&P 500 Stock Scanner{' '}
              <span style={{ 
                background: 'linear-gradient(135deg, #3B82F6 0%, #00F5FF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Pricing
              </span>
            </h1>
            <p className="text-base md:text-lg max-w-xl mx-auto" style={{ color: '#94A3B8' }}>
              Start scanning S&P 500 stocks free and upgrade when you need more. No hidden fees.
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-12">
            <div 
              className="inline-flex p-1 rounded-xl"
              style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(148, 163, 184, 0.1)' }}
            >
              <button
                onClick={() => setBillingCycle('monthly')}
                className="px-6 py-2.5 text-sm font-medium rounded-lg transition-all duration-200"
                style={{
                  background: billingCycle === 'monthly' ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                  color: billingCycle === 'monthly' ? '#60A5FA' : '#64748B',
                  border: billingCycle === 'monthly' ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid transparent'
                }}
                data-testid="billing-monthly-toggle"
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className="px-6 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-2"
                style={{
                  background: billingCycle === 'annual' ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                  color: billingCycle === 'annual' ? '#60A5FA' : '#64748B',
                  border: billingCycle === 'annual' ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid transparent'
                }}
                data-testid="billing-annual-toggle"
              >
                Annual
                <span 
                  className="px-2 py-0.5 text-xs rounded-full"
                  style={{ background: '#22C55E', color: 'white' }}
                >
                  Save $40
                </span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="flex justify-center gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <div 
              className="p-8 rounded-2xl w-full max-w-sm"
              style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(148, 163, 184, 0.1)',
                backdropFilter: 'blur(12px)'
              }}
              data-testid="pricing-card-free"
            >
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#F8FAFC' }}>Free</h3>
              <p className="text-4xl font-bold mb-1" style={{ color: '#F8FAFC' }}>
                $0 <span className="text-base font-normal" style={{ color: '#64748B' }}>forever</span>
              </p>
              <p className="text-sm mb-8" style={{ color: '#64748B' }}>Basic access to get started</p>
              
              <div className="space-y-4 mb-8">
                {(freeFeatures.length > 0 ? freeFeatures : [
                  'Limited anomaly alerts',
                  'Basic market overview',
                  'Daily scan limit',
                  'Community access'
                ]).map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#64748B' }} />
                    <span className="text-sm" style={{ color: '#94A3B8' }}>{feature}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/signup"
                className="block w-full py-3.5 rounded-xl text-center text-sm font-medium transition-colors"
                style={{
                  background: 'rgba(148, 163, 184, 0.1)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  color: '#E2E8F0'
                }}
              >
                Get Started
              </Link>
            </div>

            {/* Pro Monthly - Only visible when monthly is selected */}
            {billingCycle === 'monthly' && (
              <div 
                className="p-8 rounded-2xl relative w-full max-w-sm"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '2px solid rgba(59, 130, 246, 0.4)',
                  boxShadow: '0 0 60px rgba(59, 130, 246, 0.15)',
                  backdropFilter: 'blur(12px)'
                }}
                data-testid="pricing-card-monthly"
              >
                <div 
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                  style={{
                    background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                    color: 'white'
                  }}
                >
                  <Clock className="w-3 h-3" />
                  48-hour Free Trial
                </div>

                <h3 className="text-xl font-semibold mb-2 mt-2" style={{ color: '#F8FAFC' }}>Pro Monthly</h3>
                <p className="text-4xl font-bold mb-1" style={{ color: '#F8FAFC' }}>
                  $14.99 <span className="text-base font-normal" style={{ color: '#64748B' }}>/month</span>
                </p>
                <p className="text-sm mb-8" style={{ color: '#64748B' }}>
                  {monthlyPlan?.description || 'Start with a 48-hour free trial'}
                </p>
                
                <div className="space-y-4 mb-8">
                  {proFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <feature.icon className="w-5 h-5 flex-shrink-0" style={{ color: '#3B82F6' }} />
                      <span className="text-sm" style={{ color: '#CBD5E1' }}>{feature.text}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => handleUpgrade('pro_monthly')}
                  disabled={loading === 'pro_monthly' || isPremium}
                  className="w-full py-3.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2"
                  style={{
                    background: isPremium 
                      ? 'rgba(148, 163, 184, 0.1)' 
                      : 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                    color: 'white',
                    boxShadow: isPremium ? 'none' : '0 4px 20px rgba(59, 130, 246, 0.25)',
                    opacity: isPremium ? 0.6 : 1
                  }}
                  data-testid="subscribe-monthly-btn"
                >
                  {loading === 'pro_monthly' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : isPremium ? (
                    'Current Plan'
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      Start Free Trial
                    </>
                  )}
                </button>

                <p className="text-xs text-center mt-3" style={{ color: '#475569' }}>
                  Cancel anytime during trial
                </p>
              </div>
            )}

            {/* Pro Annual - Only visible when annual is selected */}
            {billingCycle === 'annual' && (
              <div 
                className="p-8 rounded-2xl relative w-full max-w-sm"
                style={{
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(0, 245, 255, 0.05) 100%)',
                  border: '2px solid rgba(0, 245, 255, 0.4)',
                  boxShadow: '0 0 80px rgba(0, 245, 255, 0.15), 0 0 40px rgba(0, 245, 255, 0.1), inset 0 1px 0 rgba(0, 245, 255, 0.1)',
                  backdropFilter: 'blur(12px)'
                }}
                data-testid="pricing-card-annual"
              >
                <div 
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                  style={{
                    background: 'linear-gradient(135deg, #00F5FF 0%, #3B82F6 100%)',
                    color: '#0B0F1A',
                    boxShadow: '0 4px 15px rgba(0, 245, 255, 0.4)'
                  }}
                >
                  <Sparkles className="w-3 h-3" />
                  Best Value
                </div>

              <h3 className="text-xl font-semibold mb-2 mt-2" style={{ color: '#F8FAFC' }}>Pro Annual</h3>
              <p className="text-4xl font-bold mb-1" style={{ color: '#F8FAFC' }}>
                $139.99 <span className="text-base font-normal" style={{ color: '#64748B' }}>/year</span>
              </p>
              <p className="text-sm mb-1" style={{ color: '#00F5FF' }}>
                Only $0.38 per day
              </p>
              <p className="text-xs mb-6" style={{ color: '#22C55E' }}>
                Save $39.89 vs monthly
              </p>
              
              <div className="space-y-4 mb-8">
                {proFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <feature.icon className="w-5 h-5 flex-shrink-0" style={{ color: '#00F5FF' }} />
                    <span className="text-sm" style={{ color: '#CBD5E1' }}>{feature.text}</span>
                  </div>
                ))}
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 flex-shrink-0" style={{ color: '#00F5FF' }} />
                  <span className="text-sm font-medium" style={{ color: '#00F5FF' }}>2+ months FREE</span>
                </div>
              </div>

              <button
                onClick={() => handleUpgrade('pro_annual')}
                disabled={loading === 'pro_annual' || isPremium}
                className="w-full py-3.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2"
                style={{
                  background: isPremium 
                    ? 'rgba(148, 163, 184, 0.1)' 
                    : 'linear-gradient(135deg, #00F5FF 0%, #3B82F6 100%)',
                  color: isPremium ? 'white' : '#0B0F1A',
                  boxShadow: isPremium ? 'none' : '0 4px 25px rgba(0, 245, 255, 0.35)',
                  opacity: isPremium ? 0.6 : 1,
                  fontWeight: 600
                }}
                data-testid="subscribe-annual-btn"
              >
                {loading === 'pro_annual' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : isPremium ? (
                  'Current Plan'
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Subscribe & Save
                  </>
                )}
              </button>

              <p className="text-xs text-center mt-3" style={{ color: '#94A3B8' }}>
                Billed annually at $139.99
              </p>
              </div>
            )}
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto mt-20">
            <h2 className="text-2xl font-bold text-center mb-10" style={{ color: '#F8FAFC' }}>
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              {[
                {
                  q: 'How does the 48-hour free trial work?',
                  a: 'The monthly plan includes a 48-hour free trial. Enter your payment method to start, and you won\'t be charged until after the trial. Cancel anytime during the trial at no cost.'
                },
                {
                  q: 'Does the annual plan have a trial?',
                  a: 'No, the annual plan is charged immediately but offers significant savings ($40/year off). If you\'re unsure, try the monthly plan with its free trial first.'
                },
                {
                  q: 'Can I cancel anytime?',
                  a: 'Yes! You can cancel your subscription at any time. You\'ll continue to have access until the end of your billing period.'
                },
                {
                  q: 'What payment methods do you accept?',
                  a: 'We accept all major credit cards through our secure payment processor, Stripe.'
                }
              ].map((faq, index) => (
                <div 
                  key={index}
                  className="p-6 rounded-xl"
                  style={{
                    background: 'rgba(15, 23, 42, 0.4)',
                    border: '1px solid rgba(148, 163, 184, 0.1)'
                  }}
                >
                  <h3 className="font-medium mb-2" style={{ color: '#F8FAFC' }}>{faq.q}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#94A3B8' }}>{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-12 px-6" style={{ borderTop: '1px solid rgba(148, 163, 184, 0.1)' }}>
        <div className="max-w-6xl mx-auto">
          <p className="text-sm text-center" style={{ color: '#475569' }}>
            © {new Date().getFullYear()} ScopeTheMarket. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
